import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popover,
  Row,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileAddOutlined,
  FilterOutlined,
  MergeCellsOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  SearchOutlined,
  SendOutlined,
  SplitCellsOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import Text from "antd/lib/typography/Text";
import Loading from "../../../../components/shared-components/Loading";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { isMobile } from "../../../../functions/browser";
import locale from "antd/es/date-picker/locale/nl_NL";
import styled from "styled-components";
import dayjs from "dayjs";
import { useInvoices } from "../../../../data/hooks/firestore/useInvoices";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import { getExactInvoiceById, sendOrderToExactInvoice } from "../../../../functions/api/exact";
import { useBulkExactSync } from "../../../../hooks/useBulkExactSync";
import BulkExactModal from "../../../../components/shared-components/BulkExactModal";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { useWebshopUsers } from "../../../../data/hooks/firestore/useWebshopUsers";
import CreditInvoiceModal from "./modals/CreditInvoiceModal";
import CombineInvoicesModal from "./modals/CombineInvoicesModal";
import SendInvoicModal from "./modals/SendInvoicModal";
import { Link } from "react-router-dom";
import { APP_PREFIX_PATH } from "../../../../configs/AppConfig";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { firestore, storage } from "../../../../configs/FirebaseConfig";
import roles from "../../../../roles";
import { deleteDoc, deleteField, doc, getDoc, updateDoc } from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import {
  FIRESTORE_COMPLAINTS_TABLE,
  FIRESTORE_INVOICES_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import ComplaintDetailsModal from "./modals/ComplaintDetailsModal";
import { hasDiscountPriceMismatch, splitCombinedInvoice } from "../../../../functions/invoices";
import { getBundlePackagingPrice, recalculateBundleItemPricesForOverride } from "../../../../functions/pricing";
import { GROWY } from "../../../../constants/CompanyConstants";
import useIsMobile from "../../../../hooks/useIsMobile";
import Select from "antd/es/select";
import axios from "axios";
import urls from "../../../../urls";

const { RangePicker } = DatePicker;
const { confirm } = Modal;

const Invoices = ({ companyFilter = "All", title = "Invoices" }) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [maxDaysAgo, setMaxDaysAgo] = useState(30);
  const [customDateRange, setCustomDateRange] = useState(null);
  const { data: invoices, loading: invoicesLoading } = useInvoices(maxDaysAgo, customDateRange);
  const { customers, isLoading: customersLoading } = useCustomers();
  const { webshopUsers: users } = useWebshopUsers();
  const { profile, email } = useAuth();
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [visible, setVisible] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [datePickerForm] = Form.useForm();
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [draftInvoicesTotals, setDraftInvoicesTotals] = useState({ count: 0, amount: 0 });
  const [exactLoadingOrders, setExactLoadingOrders] = useState(new Set());
  const [creditInvoiceModalOpen, setCreditInvoiceModalOpen] = useState(false);
  const [selectedInvoiceForCredit, setSelectedInvoiceForCredit] = useState(null);
  const [complaintModalVisible, setComplaintModalVisible] = useState(false);
  const [complaintLoading, setComplaintLoading] = useState(false);
  const [complaintData, setComplaintData] = useState(null);
  const [combineInvoicesModalOpen, setCombineInvoicesModalOpen] = useState(false);
  const [sendInvoicInvoice, setSendInvoicInvoice] = useState(null);
  const [manualCreditModalOpen, setManualCreditModalOpen] = useState(false);
  const [manualExactModalOpen, setManualExactModalOpen] = useState(false);
  const [selectedInvoiceForExact, setSelectedInvoiceForExact] = useState(null);
  const [manualExactForm] = Form.useForm();
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();
  const [isSyncingInvoices, setIsSyncingInvoices] = useState(false);

  // Price editing state
  const [editingPrices, setEditingPrices] = useState({}); // { productId: newPrice }
  const [editingShippingCost, setEditingShippingCost] = useState(null);
  const [isSavingPrices, setIsSavingPrices] = useState(false);
  const [editingPriceField, setEditingPriceField] = useState(null); // Which field is being edited
  const [editingShippingField, setEditingShippingField] = useState(false);

  useEffect(() => {
    if (!!invoices) {
      setIsLoading(false);
      applyFilters();
    }
  }, [invoices, companyFilter, customers, dateRange, searchText, statusFilter]);

  const applyFilters = () => {
    let filteredData = invoices || [];

    // Filter out invoices with status "combined"
    filteredData = filteredData.filter((inv) => inv.status !== "combined");

    // Filter by company first
    if (companyFilter !== "All") {
      filteredData = filteredData.filter((invoice) => {
        const customer = customers.find((c) => c.id === invoice.farmorderCustomerId);
        return customer?.company === companyFilter;
      });
    }

    // Filter by date range on farmorderDeliveryDate
    if (dateRange && dateRange.length > 0) {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      filteredData = filteredData.filter((invoice) => {
        return dayjs(invoice.farmorderDeliveryDate).isBetween(startDate, endDate, undefined, "[]");
      });
    }

    // Apply search filter
    if (searchText) {
      filteredData = utils.wildCardSearch(filteredData, searchText);
    }

    // Filter by status
    if (statusFilter !== "All") {
      filteredData = filteredData.filter((inv) => inv.status === statusFilter);
    }

    setFilteredInvoices(filteredData);

    // Calculate totals for draft invoices (filtered)
    const draftInvoices = filteredData.filter((inv) => inv.status === "draft");
    const totals = {
      count: draftInvoices.length,
      amount: draftInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
    };
    setDraftInvoicesTotals(totals);
  };

  const showDrawer = (row) => {
    setVisible(true);
    setInvoice(row);
  };

  const closeDrawer = () => {
    setVisible(false);
    setEditingPrices({});
    setEditingShippingCost(null);
    setEditingPriceField(null);
    setEditingShippingField(false);
    setInvoice(null);
  };

  // Recalculate invoice with edited prices
  const recalculatedInvoice = useMemo(() => {
    if (!invoice || (Object.keys(editingPrices).length === 0 && editingShippingCost === null)) {
      return invoice;
    }

    // Update userShoppingCart with new prices
    const updatedCart = invoice.userShoppingCart.map((item) => {
      const newPrice = editingPrices[item.productId];
      if (newPrice !== undefined) {
        return {
          ...item,
          productPrice: parseFloat(newPrice).toFixed(2),
          ...(item.isBundle && {
            bundleItems: recalculateBundleItemPricesForOverride(item, newPrice),
          }),
        };
      }
      return item;
    });

    // Recalculate subtotal
    const newSubtotal = updatedCart.reduce((acc, item) => acc + parseFloat(item.productPrice) * item.itemCount, 0);

    // Use new shipping cost or original
    const newShipping =
      editingShippingCost !== null ? parseFloat(editingShippingCost) : parseFloat(invoice.shippingCost || 0);

    // Recalculate total
    const newTotal = newSubtotal + newShipping - (invoice.discountCodeAmount || 0);

    return {
      ...invoice,
      userShoppingCart: updatedCart,
      subTotalAmount: newSubtotal,
      shippingCost: newShipping,
      totalAmount: newTotal,
    };
  }, [invoice, editingPrices, editingShippingCost]);

  const hasUnsavedPriceChanges = Object.keys(editingPrices).length > 0 || editingShippingCost !== null;

  const handleSavePriceChanges = async () => {
    if (!invoice || !hasUnsavedPriceChanges) return;

    try {
      setIsSavingPrices(true);

      await updateDoc(doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id), {
        userShoppingCart: recalculatedInvoice.userShoppingCart,
        subTotalAmount: recalculatedInvoice.subTotalAmount,
        shippingCost: parseFloat(recalculatedInvoice.shippingCost).toFixed(2),
        totalAmount: recalculatedInvoice.totalAmount,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success(t("notifications.prices_updated_successfully"));

      // Update local invoice state with new values
      setInvoice({
        ...invoice,
        userShoppingCart: recalculatedInvoice.userShoppingCart,
        subTotalAmount: recalculatedInvoice.subTotalAmount,
        shippingCost: parseFloat(recalculatedInvoice.shippingCost).toFixed(2),
        totalAmount: recalculatedInvoice.totalAmount,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      setEditingPrices({});
      setEditingShippingCost(null);
      setEditingPriceField(null);
      setEditingShippingField(false);
    } catch (error) {
      console.error("Error updating prices:", error);
      message.error(t("notifications.error_updating_prices"));
    } finally {
      setIsSavingPrices(false);
    }
  };

  const handleCancelPriceChanges = () => {
    setEditingPrices({});
    setEditingShippingCost(null);
    setEditingPriceField(null);
    setEditingShippingField(false);
  };

  const handleCloseInvoice = async (invoice) => {
    try {
      const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
      await updateDoc(invoiceRef, {
        status: "closed",
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("notifications.invoice_closed_successfully"));
    } catch (error) {
      console.error("Error closing invoice:", error);
      message.error(t("notifications.error_closing_invoice"));
    }
  };

  const handleOpenInvoice = async (invoice) => {
    try {
      const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
      await updateDoc(invoiceRef, {
        status: "draft",
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("notifications.invoice_opened_successfully"));
    } catch (error) {
      console.error("Error opening invoice:", error);
      message.error(t("notifications.error_opening_invoice"));
    }
  };

  const handleSetToDraft = async (invoice) => {
    try {
      const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
      await updateDoc(invoiceRef, {
        status: "draft",
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      message.success(t("notifications.invoice_set_to_draft_successfully"));
    } catch (error) {
      console.error("Error setting invoice to draft:", error);
      message.error(t("notifications.error_setting_invoice_to_draft"));
    }
  };

  const handleOpenManualExactModal = (invoice) => {
    setSelectedInvoiceForExact(invoice);
    manualExactForm.resetFields();
    setManualExactModalOpen(true);
  };

  const handleManualExactSubmit = async () => {
    try {
      const values = await manualExactForm.validateFields();

      const invoiceRef = doc(
        firestore,
        `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`,
        selectedInvoiceForExact.id,
      );

      await updateDoc(invoiceRef, {
        status: "in_exact",
        exactInvoiceId: values.exactInvoiceId,
        exactSalesOrderNumber: values.exactSalesOrderNumber,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success(t("notifications.invoice_marked_as_in_exact"));
      setManualExactModalOpen(false);
      setSelectedInvoiceForExact(null);
      manualExactForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        // Validation error, don't show error message
        return;
      }
      console.error("Error marking invoice as in_exact:", error);
      message.error(t("notifications.error_marking_invoice_as_in_exact"));
    }
  };

  const handleDeleteCreditInvoice = (invoice) => {
    const hasExactId = !!invoice.exactInvoiceId;

    confirm({
      title: t("confirm_delete_credit_invoice"),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>{t("delete_credit_invoice_warning")}</p>
          {hasExactId && (
            <p style={{ color: "#ff4d4f", fontWeight: "bold", marginTop: 12 }}>
              {t("delete_credit_invoice_exact_warning")}
            </p>
          )}
        </div>
      ),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          // Delete complaint images from storage if they exist
          if (invoice.complaintId) {
            const complaintRef = doc(
              firestore,
              `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_COMPLAINTS_TABLE}`,
              invoice.complaintId,
            );
            const complaintDoc = await getDoc(complaintRef);

            if (complaintDoc.exists()) {
              const complaintData = complaintDoc.data();

              // Delete images from storage
              if (complaintData.images && complaintData.images.length > 0) {
                const deleteImagePromises = complaintData.images.map(async (imageUrl) => {
                  try {
                    const imageRef = ref(storage, imageUrl);
                    await deleteObject(imageRef);
                  } catch (error) {
                    console.error(`Error deleting image ${imageUrl}:`, error);
                  }
                });

                await Promise.all(deleteImagePromises);
              }

              // Delete complaint document
              await deleteDoc(complaintRef);
            }
          }

          // Remove creditInvoiceId from original invoice
          if (invoice.originalInvoiceId) {
            const originalInvoiceRef = doc(
              firestore,
              `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`,
              invoice.originalInvoiceId,
            );
            await updateDoc(originalInvoiceRef, {
              creditInvoiceId: deleteField(),
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
          }

          // Delete credit invoice document
          const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
          await deleteDoc(invoiceRef);

          message.success(t("notifications.credit_invoice_deleted_successfully"));

          if (hasExactId) {
            message.warning(t("notifications.remember_to_delete_from_exact"), 8);
          }
        } catch (error) {
          console.error("Error deleting credit invoice:", error);
          message.error(t("notifications.error_deleting_credit_invoice"));
        }
      },
    });
  };

  const handlePeriodChange = (period) => {
    setMaxDaysAgo(period);
    setCustomDateRange(null);
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setDateRange([]);
    datePickerForm.resetFields();
  };

  const handleCustomDateRange = (range) => {
    if (range && range[0] && range[1]) {
      setCustomDateRange([range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")]);
      setMaxDaysAgo(null);
    } else {
      setCustomDateRange(null);
      setMaxDaysAgo(30);
    }
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const handleCombineInvoices = () => {
    // Validation: minimum 2 invoices selected
    if (selectedRows.length < 2) {
      message.warning(t("notifications.select_at_least_two_invoices"));
      return;
    }

    // Validation: all must be draft
    const allDraft = selectedRows.every((inv) => inv.status === "draft");
    if (!allDraft) {
      message.error(t("notifications.only_draft_invoices_can_be_combined"));
      return;
    }

    // Validation: all must be same customer
    const customerIds = new Set(selectedRows.map((inv) => inv.farmorderCustomerId));
    if (customerIds.size > 1) {
      message.error(t("notifications.invoices_must_be_same_customer"));
      return;
    }

    // Open combine modal
    setCombineInvoicesModalOpen(true);
  };

  const handleCombineSuccess = (combinedInvoiceId) => {
    setCombineInvoicesModalOpen(false);
    setSelectedRows([]);
    setSelectedRowKeys([]);
    message.success(t("notifications.invoices_combined_successfully"));
  };

  const handleSplitCombinedInvoice = (invoice) => {
    confirm({
      title: t("confirm_split_combined_invoice"),
      icon: <ExclamationCircleOutlined />,
      content: t("split_combined_invoice_warning"),
      okText: t("split"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          await splitCombinedInvoice(invoice.id);
          message.success(t("notifications.combined_invoice_split_successfully"));
        } catch (error) {
          console.error("Error splitting combined invoice:", error);
          message.error(t("notifications.error_splitting_combined_invoice") + ": " + error.message);
        }
      },
    });
  };

  const dropdownMenu = (row) => {
    const isCreditInvoice = row.invoiceType === "credit";
    const isCombinedInvoice = row.isCombinedInvoice === true;
    const isInExact = row.status === "in_exact";
    const isDraft = row.status === "draft";
    const isClosed = row.status === "closed";
    const isTimeout = row.status === "timeout";
    const isSyncFailed = row.status === "sync_failed";
    const isSyncExcluded = row.status === "sync_excluded";
    const isSyncing = row.status === "syncing";

    // EDI-klanten: INVOIC staat op de factuur zodra de trigger hem heeft gegenereerd
    const sendInvoicItem = row.edifactInvoicMessage && (
      <Menu.Item key="send_invoic">
        <a onClick={() => setSendInvoicInvoice(row)}>
          <Flex alignItems="center">
            <SendOutlined />
            <span style={{ marginLeft: 4 }}>{t(row.edifactInvoicSentAt ? "send_invoic_again" : "send_invoic")}</span>
          </Flex>
        </a>
      </Menu.Item>
    );

    // Ready to sync or Sync failed/excluded invoice menu
    if (isSyncFailed || isSyncExcluded || isSyncing) {
      return (
        <Menu>
          <Menu.Item key={1}>
            <a onClick={() => showDrawer(row)}>
              <Flex alignItems="center">
                <EyeOutlined />
                <span style={{ marginLeft: 4 }}>{t("details")}</span>
              </Flex>
            </a>
          </Menu.Item>
          {sendInvoicItem}
          <Menu.Item key={2}>
            <a onClick={() => handleSetToDraft(row)}>
              <Flex alignItems="center">
                <CheckOutlined />
                <span style={{ marginLeft: 4 }}>{t("set_to_draft")}</span>
              </Flex>
            </a>
          </Menu.Item>
        </Menu>
      );
    }

    if (isCreditInvoice) {
      return (
        <Menu>
          <Menu.Item key={1}>
            <a onClick={() => showDrawer(row)}>
              <Flex alignItems="center">
                <EyeOutlined />
                <span style={{ marginLeft: 4 }}>{t("details")}</span>
              </Flex>
            </a>
          </Menu.Item>
          <Menu.Item key={2}>
            <a onClick={() => handleDeleteCreditInvoice(row)}>
              <Flex alignItems="center">
                <DeleteOutlined />
                <span style={{ marginLeft: 4 }}>{t("delete_credit_invoice")}</span>
              </Flex>
            </a>
          </Menu.Item>
        </Menu>
      );
    }

    // Timeout or Draft invoice menu with manual exact option
    if (isTimeout || isDraft) {
      const hasExistingCredits = row.userShoppingCart?.some((product) => product.itemCount < 0 && product.isCreditLine);
      const hasCreditInvoiceCreated = !!row.creditInvoiceId;

      return (
        <Menu>
          <Menu.Item key={1}>
            <a onClick={() => showDrawer(row)}>
              <Flex alignItems="center">
                <EyeOutlined />
                <span style={{ marginLeft: 4 }}>{t("details")}</span>
              </Flex>
            </a>
          </Menu.Item>
          {sendInvoicItem}
          <Menu.Item key={2}>
            {hasCreditInvoiceCreated ? (
              <a onClick={() => handleViewCreditInvoice(row)}>
                <Flex alignItems="center">
                  <EyeOutlined />
                  <span style={{ marginLeft: 4 }}>{t("view_credit_invoice")}</span>
                </Flex>
              </a>
            ) : (
              <a onClick={() => handleCreateCreditInvoice(row)}>
                <Flex alignItems="center">
                  <FileAddOutlined />
                  <span style={{ marginLeft: 4 }}>
                    {hasExistingCredits ? t("adjust_credit_invoice") : t("create_credit_invoice")}
                  </span>
                </Flex>
              </a>
            )}
          </Menu.Item>
          {isTimeout && (
            <Menu.Item key={3}>
              <a onClick={() => handleOpenInvoice(row)}>
                <Flex alignItems="center">
                  <CheckOutlined />
                  <span style={{ marginLeft: 4 }}>{t("set_to_draft")}</span>
                </Flex>
              </a>
            </Menu.Item>
          )}
          {isCombinedInvoice && (
            <Menu.Item key={3}>
              <a onClick={() => handleSplitCombinedInvoice(row)}>
                <Flex alignItems="center">
                  <SplitCellsOutlined />
                  <span style={{ marginLeft: 4 }}>{t("split_combined_invoice")}</span>
                </Flex>
              </a>
            </Menu.Item>
          )}
          <Menu.Item key={4}>
            <a onClick={() => handleOpenManualExactModal(row)}>
              <Flex alignItems="center">
                <CheckOutlined />
                <span style={{ marginLeft: 4 }}>{t("set_to_in_exact")}</span>
              </Flex>
            </a>
          </Menu.Item>
          {isDraft && (
            <Menu.Item key={5}>
              <a onClick={() => handleCloseInvoice(row)}>
                <Flex alignItems="center">
                  <CloseOutlined />
                  <span style={{ marginLeft: 4 }}>{t("close_invoice")}</span>
                </Flex>
              </a>
            </Menu.Item>
          )}
        </Menu>
      );
    }

    // Regular invoice menu (closed or in_exact)
    const hasExistingCredits = row.userShoppingCart?.some((product) => product.itemCount < 0 && product.isCreditLine);
    const hasCreditInvoiceCreated = !!row.creditInvoiceId;

    return (
      <Menu>
        <Menu.Item key={1}>
          <a onClick={() => showDrawer(row)}>
            <Flex alignItems="center">
              <EyeOutlined />
              <span style={{ marginLeft: 4 }}>{t("details")}</span>
            </Flex>
          </a>
        </Menu.Item>
        {sendInvoicItem}
        {!isTimeout && (
          <Menu.Item key={2}>
            {hasCreditInvoiceCreated ? (
              <a onClick={() => handleViewCreditInvoice(row)}>
                <Flex alignItems="center">
                  <EyeOutlined />
                  <span style={{ marginLeft: 4 }}>{t("view_credit_invoice")}</span>
                </Flex>
              </a>
            ) : (
              <a onClick={() => handleCreateCreditInvoice(row)}>
                <Flex alignItems="center">
                  <FileAddOutlined />
                  <span style={{ marginLeft: 4 }}>
                    {hasExistingCredits ? t("adjust_credit_invoice") : t("create_credit_invoice")}
                  </span>
                </Flex>
              </a>
            )}
          </Menu.Item>
        )}
        {isCombinedInvoice && (
          <Menu.Item key={3}>
            <a onClick={() => handleSplitCombinedInvoice(row)}>
              <Flex alignItems="center">
                <DeleteOutlined />
                <span style={{ marginLeft: 4 }}>{t("split_combined_invoice")}</span>
              </Flex>
            </a>
          </Menu.Item>
        )}
        {isInExact && (
          <Menu.Item key={4}>
            <a onClick={() => handleRevertSingleInvoice(row)}>
              <Flex alignItems="center">
                <CloseOutlined />
                <span style={{ marginLeft: 4 }}>{t("revert_from_exact")}</span>
              </Flex>
            </a>
          </Menu.Item>
        )}
        {!isInExact && isClosed && (
          <Menu.Item key={5}>
            <a onClick={() => handleOpenInvoice(row)}>
              <Flex alignItems="center">
                <CheckOutlined />
                <span style={{ marginLeft: 4 }}>{t("open_invoice")}</span>
              </Flex>
            </a>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleViewCreditInvoice = async (invoice) => {
    try {
      const creditInvoiceRef = doc(
        firestore,
        `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`,
        invoice.creditInvoiceId,
      );
      const creditInvoiceDoc = await getDoc(creditInvoiceRef);

      if (creditInvoiceDoc.exists()) {
        const creditInvoiceData = { id: creditInvoiceDoc.id, ...creditInvoiceDoc.data() };
        showDrawer(creditInvoiceData);
      } else {
        messageApi.error(t("notifications.credit_invoice_not_found"));
      }
    } catch (error) {
      console.error("Error loading credit invoice:", error);
      messageApi.error(t("notifications.error_loading_credit_invoice"));
    }
  };

  const handleCreateCreditInvoice = async (invoice) => {
    try {
      const token = await getFirebaseIDToken();
      let invoiceStatus = "open";
      let exactInvoiceData = null;

      if (invoice.status === "draft") {
        invoiceStatus = "draft";
      }

      if (invoice.status === "in_exact") {
        try {
          exactInvoiceData = await getExactInvoiceById(invoice.exactInvoiceId, token);
        } catch (error) {
          const statusCode = error?.response?.status;
          if (statusCode === 404 || statusCode === 400) {
            confirm({
              title: t("confirm_invoice_not_found_in_exact_title"),
              icon: <ExclamationCircleOutlined />,
              content: t("confirm_invoice_not_found_in_exact_message"),
              okText: t("create_credit_invoice"),
              cancelText: t("cancel"),
              onOk: async () => {
                setSelectedInvoiceForCredit({
                  ...invoice,
                  invoiceStatus: "processed",
                  exactInvoiceData: null,
                });
                setCreditInvoiceModalOpen(true);
              },
            });
            return;
          }

          console.error("Error fetching exact invoice:", error);
          messageApi.error(t("notifications.failed_to_fetch_invoice_data"));
          return;
        }
      }

      setSelectedInvoiceForCredit({
        ...invoice,
        invoiceStatus,
        exactInvoiceData,
      });
      setCreditInvoiceModalOpen(true);
    } catch (error) {
      console.error("Error in handleCreateCreditInvoice:", error);
      messageApi.error(t("notifications.error_fetching_invoice_data"));
    }
  };

  const openComplaintDetails = async (complaintId) => {
    try {
      setComplaintLoading(true);
      setComplaintData(null);
      const ref = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_COMPLAINTS_TABLE}`, complaintId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setComplaintData({ id: complaintId, ...snap.data() });
      } else {
        messageApi.error(t("notifications.complaint_not_found"));
      }
      setComplaintModalVisible(true);
    } catch (err) {
      console.error("Error loading complaint", err);
      messageApi.error(t("notifications.error_loading_complaint"));
    } finally {
      setComplaintLoading(false);
    }
  };

  const handleCloseCreditInvoiceModal = () => {
    setCreditInvoiceModalOpen(false);
    setSelectedInvoiceForCredit(null);
  };

  const getInvoiceStatusColor = (status) => {
    if (status === "draft") return "orange";
    if (status === "sync_failed") return "red";
    if (status === "sync_excluded") return "gray";
    if (status === "syncing") return "purple";
    if (status === "in_exact") return "green";
    if (status === "closed") return "red";
    if (status === "timeout") return "blue";
    return "default";
  };

  const getInvoiceStatusText = (status) => {
    if (status === "draft") return t("draft");
    if (status === "sync_failed") return t("sync_failed");
    if (status === "sync_excluded") return t("sync_excluded");
    if (status === "syncing")
      return (
        <>
          {t("syncing_no_dots")} <SyncOutlined spin />
        </>
      );
    if (status === "in_exact") return t("in_exact");
    if (status === "closed") return t("closed");
    if (status === "timeout") return t("timeout");
    return status;
  };

  const statusMenu = (
    <Menu
      onClick={({ key }) => {
        setStatusFilter(key);
      }}
    >
      <Menu.Item key="All">{t("all")}</Menu.Item>
      <Menu.Item key="draft">{getInvoiceStatusText("draft")}</Menu.Item>
      <Menu.Item key="sync_failed">{getInvoiceStatusText("sync_failed")}</Menu.Item>
      <Menu.Item key="sync_excluded">{getInvoiceStatusText("sync_excluded")}</Menu.Item>
      <Menu.Item key="syncing">{t("syncing_no_dots")}</Menu.Item>
      <Menu.Item key="in_exact">{getInvoiceStatusText("in_exact")}</Menu.Item>
      <Menu.Item key="closed">{getInvoiceStatusText("closed")}</Menu.Item>
      <Menu.Item key="timeout">{getInvoiceStatusText("timeout")}</Menu.Item>
    </Menu>
  );

  const exactStatusMenu = (data) => (
    <Menu>
      <Menu.Item>
        <a onClick={(e) => handleSendToExactInvoice(e, data)}>{t("send_to_exact")}</a>
      </Menu.Item>
    </Menu>
  );

  const handleSendToExactInvoice = async (e, data) => {
    e.preventDefault();

    setExactLoadingOrders((prev) => new Set(prev).add(data.id));

    try {
      const token = await getFirebaseIDToken();

      const transformedData = {
        ...data,
        orderDateTime: new Date(data.orderDateTime).toISOString().split("T")[0],
        userEmail: profile.userEmail,
      };

      await sendOrderToExactInvoice(transformedData, token);
      message.success(t("notifications.order_saved_to_exact"));
    } catch (err) {
      console.log("Full error:", err);
      message.error(t("notifications.error_save_order_to_exact"));
    } finally {
      setExactLoadingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(data.id);
        return newSet;
      });
    }
  };

  const handleManualSyncTrigger = () => {
    const hasSelectedInvoices = selectedRows.length > 0;
    const companyName = companyFilter === GROWY ? "Growy" : companyFilter === "CF" ? "Chef's Farm" : "";

    let messageKey;
    let invoiceCount;

    if (hasSelectedInvoices) {
      // Only sync selected invoices
      const eligibleInvoices = selectedRows.filter((inv) => inv.status === "draft" && !inv.exactInvoiceId);
      invoiceCount = eligibleInvoices.length;

      if (invoiceCount === 0) {
        message.warning(t("notifications.no_eligible_invoices_selected"));
        return;
      }

      messageKey = "bulk_sync_exact_message_selected";
    } else {
      // Sync all draft invoices (existing behavior)
      messageKey =
        companyFilter && companyFilter !== "All" ? "bulk_sync_exact_message_company" : "bulk_sync_exact_message";
    }

    confirm({
      title: t("bulk_sync_exact_title"),
      content: hasSelectedInvoices ? t(messageKey, { count: invoiceCount }) : t(messageKey, { company: companyName }),
      okText: t("proceed"),
      cancelText: t("cancel"),
      onOk: () => {
        setIsSyncingInvoices(true);
        (async () => {
          try {
            const token = await getFirebaseIDToken();

            if (hasSelectedInvoices) {
              // Use specific invoice sync endpoint
              const eligibleInvoices = selectedRows.filter((inv) => inv.status === "draft" && !inv.exactInvoiceId);
              const invoiceIds = eligibleInvoices.map((inv) => inv.id);

              const url = `${urls.api}/api/exact/invoices/sync/specific`;
              const response = await axios.post(
                url,
                {
                  invoiceIds,
                  region: "NL",
                },
                {
                  headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                  },
                },
              );

              // Show detailed results
              const { successful, failed, totalRequested } = response.data;
              if (successful > 0) {
                message.success(t("notifications.invoices_synced_successfully", { count: successful }));
              }
              if (failed > 0) {
                message.warning(t("notifications.invoices_sync_failed", { count: failed }));
              }

              // Clear selection after sync
              setSelectedRows([]);
              setSelectedRowKeys([]);
            } else {
              // Use bulk sync endpoint (existing behavior)
              const params = new URLSearchParams();
              if (companyFilter && companyFilter !== "All") {
                params.append("company", companyFilter);
              }
              const url = `${urls.api}/api/exact/invoices/sync${params.toString() ? `?${params.toString()}` : ""}`;
              await axios.get(url, {
                headers: {
                  Authorization: token,
                },
              });
              message.success(t("notifications.sync_triggered_successfully"));
            }
          } catch (error) {
            console.error("Error triggering invoice sync:", error);
            message.error(t("notifications.error_triggering_invoice_sync"));
          } finally {
            setIsSyncingInvoices(false);
          }
        })();
      },
    });
  };

  const handleRevertSingleInvoice = async (invoice) => {
    confirm({
      title: t("revert_exact_title"),
      content: t("revert_exact_message_selected", { count: 1 }),
      okText: t("proceed"),
      cancelText: t("cancel"),
      onOk: async () => {
        try {
          const invoiceRef = doc(firestore, `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`, invoice.id);
          await updateDoc(invoiceRef, {
            status: "draft",
            exactSalesOrderNumber: deleteField(),
            exactInvoiceNumber: deleteField(),
            exactInvoiceId: deleteField(),
          });
          message.success(t("notifications.invoices_reverted_successfully", { count: 1 }));
        } catch (error) {
          console.error(`Error reverting invoice ${invoice.id}:`, error);
          message.error(t("notifications.error_reverting_invoices"));
        }
      },
    });
  };

  const handleRevertFromExact = () => {
    const hasSelectedInvoices = selectedRows.length > 0;

    let invoiceCount;
    let eligibleInvoices;

    if (hasSelectedInvoices) {
      // Only revert selected invoices with status "in_exact"
      eligibleInvoices = selectedRows.filter((inv) => inv.status === "in_exact");
      invoiceCount = eligibleInvoices.length;

      if (invoiceCount === 0) {
        message.warning(t("no_eligible_invoices_for_revert"));
        return;
      }
    } else {
      // Revert all in_exact invoices
      eligibleInvoices = filteredInvoices.filter((inv) => inv.status === "in_exact");
      invoiceCount = eligibleInvoices.length;

      if (invoiceCount === 0) {
        message.warning(t("no_eligible_invoices_for_revert"));
        return;
      }
    }

    const messageKey = hasSelectedInvoices ? "revert_exact_message_selected" : "revert_exact_message";

    confirm({
      title: t("revert_exact_title"),
      content: t(messageKey, { count: invoiceCount }),
      okText: t("proceed"),
      cancelText: t("cancel"),
      onOk: async () => {
        setIsSyncingInvoices(true);
        try {
          let successCount = 0;
          let failCount = 0;

          for (const invoice of eligibleInvoices) {
            try {
              const invoiceRef = doc(
                firestore,
                `${FIRESTORE_REGIONS_TABLE}/NL/${FIRESTORE_INVOICES_TABLE}`,
                invoice.id,
              );
              await updateDoc(invoiceRef, {
                status: "draft",
                exactSalesOrderNumber: deleteField(),
                exactInvoiceNumber: deleteField(),
                exactInvoiceId: deleteField(),
              });
              successCount++;
            } catch (error) {
              console.error(`Error reverting invoice ${invoice.id}:`, error);
              failCount++;
            }
          }

          if (successCount > 0) {
            message.success(t("notifications.invoices_reverted_successfully", { count: successCount }));
          }
          if (failCount > 0) {
            message.warning(t("notifications.invoices_revert_failed", { count: failCount }));
          }

          // Clear selection after revert
          setSelectedRows([]);
          setSelectedRowKeys([]);
        } catch (error) {
          console.error("Error reverting invoices:", error);
          message.error(t("notifications.error_reverting_invoices"));
        } finally {
          setIsSyncingInvoices(false);
        }
      },
    });
  };

  // Check if there are any eligible selected invoices that can be synced
  const hasEligibleSelectedInvoices = useMemo(() => {
    if (selectedRows.length === 0) return false;
    return selectedRows.some(
      (inv) =>
        !inv.exactInvoiceId && // Must not already have exact ID
        inv.status !== "closed" &&
        inv.status !== "timeout" &&
        inv.status !== "sync_failed" &&
        inv.status !== "sync_excluded" &&
        inv.status !== "syncing" &&
        !hasDiscountPriceMismatch(inv),
    );
  }, [selectedRows]);

  // Expandable row for combined invoices
  const expandedRowRender = (record) => {
    if (!record.isCombinedInvoice || !record.combinedInvoiceDetails) return null;

    const subInvoiceColumns = [
      {
        title: t("original_invoice_id"),
        dataIndex: "originalInvoiceId",
        render: (id) => <span style={{ fontFamily: "monospace", fontSize: "11px" }}>{id}</span>,
      },
      {
        title: t("order_id"),
        dataIndex: "orderId",
      },
      {
        title: t("delivery_date"),
        dataIndex: "deliveryDate",
        render: (date) => dayjs(date).format("D MMM YYYY"),
      },
      {
        title: t("products"),
        dataIndex: "products",
        render: (products) => `${products?.length || 0} items`,
      },
      {
        title: t("reference"),
        dataIndex: "reference",
        render: (ref) => ref || "-",
      },
      {
        title: t("subtotal"),
        dataIndex: "subTotal",
        render: (amount) => CURRENCY + formatPrice(amount),
      },
      {
        title: t("shipping"),
        dataIndex: "shippingCost",
        render: (amount) => CURRENCY + formatPrice(amount),
      },
    ];

    return (
      <div style={{ padding: "12px 24px", backgroundColor: "#fafafa" }}>
        <Table
          columns={subInvoiceColumns}
          dataSource={record.combinedInvoiceDetails}
          pagination={false}
          size="small"
          rowKey="originalInvoiceId"
        />
      </div>
    );
  };

  const tableColumns = [
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
    {
      title: t("order_date"),
      dataIndex: "orderDateTime",
      sorter: (a, b) => Number(a.orderDateTime ?? 0) - Number(b.orderDateTime ?? 0),
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          <span style={{ minWidth: "120px", display: "block" }}>
            {(() => {
              const ts = data.orderDateTime;
              if (ts === null || ts === undefined) return "-";
              const d = dayjs(Number(ts));
              return d.isValid() ? d.format("D MMM YYYY HH:mm") : "-";
            })()}
          </span>
        </button>
      ),
      defaultSortOrder: "descend",
    },
    {
      title: t("company"),
      dataIndex: "farmorderCustomerName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderCustomerName"),
      render: (_, data) => (
        <span>
          {data.farmorderCustomerName}{" "}
          {companyFilter === GROWY
            ? ""
            : !!data.farmorderUserFirstName
              ? `(${data.farmorderUserFirstName} ${data.farmorderUserLastName})`
              : `(${data.farmorderUserId})`}
        </span>
      ),
    },
    {
      title: t("delivery_date"),
      dataIndex: "farmorderDeliveryDate",
      render: (date) => (
        <span style={{ minWidth: "110px", display: "block" }}>
          {dayjs(date, "YYYY-MM-DD").format("ddd D MMM YYYY")}
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderDeliveryDate"),
    },
    {
      title: t("status"),
      dataIndex: "status",
      render: (status) => (
        <span>
          <Tag color={getInvoiceStatusColor(status)}>{getInvoiceStatusText(status)}</Tag>
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "status"),
    },
    {
      title: t("exact_id"),
      dataIndex: "exactId",
      render: (_, data) =>
        !!data.exactInvoiceId ? (
          <span style={{ minWidth: "55px", display: "block" }}>{data.exactInvoiceId}</span>
        ) : data.status === "closed" || data.status === "timeout" || hasDiscountPriceMismatch(data) ? (
          <span style={{ minWidth: "55px", display: "block" }}>-</span>
        ) : data.status === "syncing" ? (
          <span
            style={{
              minWidth: "55px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: 0.5,
              cursor: "not-allowed",
            }}
          >
            {t("no")}
            {exactLoadingOrders.has(data.id) && <SyncOutlined spin />}
          </span>
        ) : (
          <Dropdown overlay={exactStatusMenu(data)} trigger={["click"]}>
            <a onClick={(e) => e.preventDefault}>
              <span style={{ minWidth: "55px", display: "flex", alignItems: "center", gap: "6px" }}>
                {t("no")}
                {exactLoadingOrders.has(data.id) && <SyncOutlined spin />}
              </span>
            </a>
          </Dropdown>
        ),
    },
    {
      title: t("sales_id"),
      dataIndex: "exactSalesOrderNumber",
      render: (salesId) => <span>{!!salesId ? salesId : "-"}</span>,
    },
    {
      title: t("invoice_type"),
      dataIndex: "invoiceType",
      render: (method) => <span>{!!method ? t("credit") : t("debit") || "-"}</span>,
    },
    {
      title: t("total"),
      dataIndex: "totalAmount",
      render: (amount, record) => (
        <span style={{ minWidth: "65px", display: "flex", alignItems: "center", gap: "6px" }}>
          {CURRENCY + formatPrice(amount)}
          {hasDiscountPriceMismatch(record) && (
            <Tooltip title={t("notifications.discount_price_mismatch_warning")}>
              <WarningOutlined style={{ color: "#faad14", fontSize: "16px" }} />
            </Tooltip>
          )}
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "totalAmount"),
    },
  ];

  const tableColumnsDetails = [
    {
      title: t("product"),
      dataIndex: "productName",
    },
    {
      title: t("quantity"),
      dataIndex: "itemCount",
    },
    {
      title: t("price"),
      dataIndex: "productPrice",
      render: (data, record) => {
        const canEdit = profile?.userRole === roles.superAdmin && invoice?.status === "draft";
        const isEditing = editingPriceField === record.productId;
        const hasOverride = editingPrices[record.productId] !== undefined;
        const displayPrice = hasOverride ? editingPrices[record.productId] : data;

        if (!canEdit) {
          return <span>{CURRENCY + formatPrice(data)}</span>;
        }

        if (isEditing) {
          const packagingPrice = record.isBundle ? getBundlePackagingPrice(record) : 0;
          return (
            <InputNumber
              autoFocus
              value={parseFloat(displayPrice)}
              onChange={(value) => {
                if (value !== null) {
                  setEditingPrices((prev) => ({ ...prev, [record.productId]: value }));
                }
              }}
              onBlur={() => {
                setEditingPriceField(null);
                // Validate bundle packaging price
                if (record.isBundle && packagingPrice > 0) {
                  const currentPrice = editingPrices[record.productId];
                  if (currentPrice !== undefined && parseFloat(currentPrice) < packagingPrice) {
                    setEditingPrices((prev) => ({
                      ...prev,
                      [record.productId]: packagingPrice.toFixed(2),
                    }));
                    message.warning(t("bundle_price_below_packaging"));
                  }
                }
              }}
              onPressEnter={() => {
                setEditingPriceField(null);
                // Validate bundle packaging price
                if (record.isBundle && packagingPrice > 0) {
                  const currentPrice = editingPrices[record.productId];
                  if (currentPrice !== undefined && parseFloat(currentPrice) < packagingPrice) {
                    setEditingPrices((prev) => ({
                      ...prev,
                      [record.productId]: packagingPrice.toFixed(2),
                    }));
                    message.warning(t("bundle_price_below_packaging"));
                  }
                }
              }}
              precision={2}
              min={0}
              step={0.01}
              style={{ width: "100px" }}
              prefix="€"
            />
          );
        }

        return (
          <span
            onClick={() => setEditingPriceField(record.productId)}
            style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dashed" }}
          >
            {CURRENCY + formatPrice(displayPrice)}
          </span>
        );
      },
    },
    {
      title: t("sub_total"),
      render: (_, item) => {
        const price =
          editingPrices[item.productId] !== undefined ? editingPrices[item.productId] : parseFloat(item.productPrice);
        const calculatedSubTotal = price * item.itemCount;
        if (!item || !item.itemCount) {
          return <span>{CURRENCY + 0}</span>;
        }
        return <span>{CURRENCY + formatPrice(calculatedSubTotal)}</span>;
      },
    },
  ];

  const onSearch = (value) => {
    setSearchText(value);
  };

  const rowSelection = {
    onChange: (key, rows) => {
      setSelectedRows(rows);
      setSelectedRowKeys(key);
    },
  };

  const handleDateRange = (dateRange) => {
    setDateRange(dateRange);
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const {
    bulkExactModalVisible,
    bulkExactProgress,
    bulkExactCurrentItem,
    bulkExactResults,
    bulkExactProcessing,
    handleBulkSendToExact,
    closeBulkExactModal,
  } = useBulkExactSync();

  const handleBulkSendToExactInvoices = async () => {
    // Filter out closed, timeout invoices and invoices with discount price mismatch
    const eligibleInvoices = filteredInvoices.filter(
      (inv) =>
        inv.status !== "closed" &&
        inv.status !== "timeout" &&
        inv.status !== "sync_failed" &&
        inv.status !== "sync_excluded" &&
        inv.status !== "syncing" &&
        !hasDiscountPriceMismatch(inv),
    );
    const eligibleSelectedRows = selectedRows.filter(
      (inv) =>
        inv.status !== "closed" &&
        inv.status !== "timeout" &&
        inv.status !== "sync_failed" &&
        inv.status !== "sync_excluded" &&
        inv.status !== "syncing" &&
        !hasDiscountPriceMismatch(inv),
    );

    await handleBulkSendToExact({
      items: eligibleInvoices,
      dateRange,
      selectedRows: eligibleSelectedRows,
      filterKey: "exactInvoiceId",
      sendFunction: sendOrderToExactInvoice,
      transformData: (invoice) => ({
        ...invoice,
        orderDateTime: new Date(invoice.orderDateTime).toISOString().split("T")[0],
      }),
      confirmTitle: t("confirm_bulk_send_exact_invoices"),
      confirmMessage: "confirm_bulk_send_exact_invoices_message",
      itemType: "invoice",
    });
  };

  return (
    <>
      {contextHolder}
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t(title)}</h1>
        </Col>
        <Col xs={24}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8} md={6}>
              <Card>
                <Statistic
                  title={t("draft_invoices_count")}
                  value={draftInvoicesTotals.count}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Card>
                <Statistic
                  title={t("draft_invoices_total")}
                  value={draftInvoicesTotals.amount}
                  precision={2}
                  prefix={CURRENCY}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
          </Row>

          <Card>
            <Row gutter={[16, 8]} style={{ justifyContent: "space-between", marginBottom: ".5rem" }}>
              {!isMobileView ? (
                <>
                  <Input
                    placeholder={t("search")}
                    style={{ width: "fit-content", maxHeight: 40, marginTop: "1rem" }}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => onSearch(e.target.value)}
                    allowClear
                  />
                  <ButtonsWrapper>
                    <Dropdown overlay={statusMenu} trigger={["click"]}>
                      <Button>
                        {t("status")}: {statusFilter === "All" ? t("all") : getInvoiceStatusText(statusFilter)}{" "}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                    <Tooltip title={t("filter_on_delivery_date")}>
                      <Form form={datePickerForm}>
                        <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                          <RangePicker
                            onChange={(e) => handleDateRange(e)}
                            locale={locale}
                            placeholder={[t("start"), t("end")]}
                            format={"DD-MM-YYYY"}
                            style={{ width: "145px" }}
                          />
                        </Form.Item>
                      </Form>
                    </Tooltip>
                    {companyFilter === GROWY && (
                      <Button
                        type="default"
                        onClick={handleCombineInvoices}
                        disabled={selectedRowKeys.length < 2}
                        icon={<MergeCellsOutlined />}
                      >
                        {t("combine_invoices")}
                      </Button>
                    )}
                    <Button type="default" onClick={() => setManualCreditModalOpen(true)} icon={<FileAddOutlined />}>
                      {t("create_credit_invoice")}
                    </Button>
                    {/*<Button*/}
                    {/*  type="primary"*/}
                    {/*  onClick={handleBulkSendToExactInvoices}*/}
                    {/*  disabled={!hasEligibleSelectedInvoices && (!dateRange || dateRange.length === 0)}*/}
                    {/*>*/}
                    {/*  <SyncOutlined /> {t("send_to_exact_bulk")}*/}
                    {/*</Button>*/}
                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item key="send" icon={<SyncOutlined />} onClick={handleManualSyncTrigger}>
                            {t("send_to_exact")}
                          </Menu.Item>
                          <Menu.Item key="revert" icon={<CloseOutlined />} onClick={handleRevertFromExact}>
                            {t("revert_from_exact")}
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={["click"]}
                      disabled={isSyncingInvoices}
                    >
                      <Button type="default" loading={isSyncingInvoices} style={{ marginLeft: "auto" }}>
                        {t("bulk_operations")} <DownOutlined />
                      </Button>
                    </Dropdown>
                  </ButtonsWrapper>
                </>
              ) : (
                <>
                  <div style={{ marginTop: "1rem" }}>
                    <Popover
                      content={
                        <FilterPopoverContent>
                          <div>
                            <FilterLabel>{t("search")}</FilterLabel>
                            <Input
                              placeholder={t("search")}
                              prefix={<SearchOutlined />}
                              value={searchText}
                              onChange={(e) => onSearch(e.target.value)}
                              allowClear
                              size="large"
                            />
                          </div>
                          <div>
                            <FilterLabel>{t("status")}</FilterLabel>
                            <Select
                              value={statusFilter}
                              onChange={setStatusFilter}
                              style={{ width: "100%" }}
                              size="large"
                            >
                              <Select.Option value="All">{t("all")}</Select.Option>
                              <Select.Option value="draft">{getInvoiceStatusText("draft")}</Select.Option>
                              <Select.Option value="sync_failed">{getInvoiceStatusText("sync_failed")}</Select.Option>
                              <Select.Option value="sync_excluded">
                                {getInvoiceStatusText("sync_excluded")}
                              </Select.Option>
                              <Select.Option value="syncing">{t("syncing_no_dots")}</Select.Option>
                              <Select.Option value="in_exact">{getInvoiceStatusText("in_exact")}</Select.Option>
                              <Select.Option value="closed">{getInvoiceStatusText("closed")}</Select.Option>
                              <Select.Option value="timeout">{getInvoiceStatusText("timeout")}</Select.Option>
                            </Select>
                          </div>
                          <div style={{ marginTop: "1rem" }}>
                            <FilterLabel>{t("filter_on_delivery_date")}</FilterLabel>
                            <Form form={datePickerForm}>
                              <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                                <RangePicker
                                  onChange={(e) => handleDateRange(e)}
                                  locale={locale}
                                  placeholder={[t("start"), t("end")]}
                                  format={"DD-MM-YYYY"}
                                  style={{ width: "100%" }}
                                  size="large"
                                  popupClassName="single-month-range-picker"
                                />
                              </Form.Item>
                            </Form>
                          </div>
                          <FilterButtonsContainer>
                            <Button
                              onClick={() => {
                                setSearchText("");
                                setStatusFilter("All");
                                datePickerForm.resetFields();
                                handleDateRange(null);
                                setFilterPopoverVisible(false);
                              }}
                              style={{ flex: 1 }}
                            >
                              {t("reset")}
                            </Button>
                            <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                              {t("apply")}
                            </Button>
                          </FilterButtonsContainer>
                        </FilterPopoverContent>
                      }
                      title={t("filters")}
                      trigger="click"
                      open={filterPopoverVisible}
                      onOpenChange={setFilterPopoverVisible}
                      placement="bottomLeft"
                    >
                      <Button
                        icon={<FilterOutlined />}
                        style={{
                          borderColor: searchText || statusFilter !== "All" || dateRange?.length > 0 ? "#FF4B33" : "",
                          color: searchText || statusFilter !== "All" || dateRange?.length > 0 ? "#FF4B33" : "",
                        }}
                      />
                    </Popover>
                  </div>
                  <ButtonsWrapper>
                    {!isMobile && (
                      <Tooltip title={t("filter_on_delivery_date")}>
                        <Form form={datePickerForm}>
                          <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                            <RangePicker
                              onChange={(e) => handleDateRange(e)}
                              locale={locale}
                              placeholder={[t("start"), t("end")]}
                              format={"DD-MM-YYYY"}
                              style={{ width: "145px" }}
                            />
                          </Form.Item>
                        </Form>
                      </Tooltip>
                    )}
                    {companyFilter === GROWY && (
                      <Button
                        type="default"
                        onClick={handleCombineInvoices}
                        disabled={selectedRowKeys.length < 2}
                        icon={<MergeCellsOutlined />}
                      >
                        {!isMobile && t("combine_invoices")}
                      </Button>
                    )}
                    <Button type="default" onClick={() => setManualCreditModalOpen(true)} icon={<FileAddOutlined />}>
                      {!isMobile && t("create_credit_invoice")}
                    </Button>
                    {/*<Button*/}
                    {/*  type="primary"*/}
                    {/*  onClick={handleBulkSendToExactInvoices}*/}
                    {/*  disabled={!hasEligibleSelectedInvoices && (!dateRange || dateRange.length === 0)}*/}
                    {/*>*/}
                    {/*  <SyncOutlined /> {!isMobile && t("send_to_exact_bulk")}*/}
                    {/*</Button>*/}
                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item key="send" icon={<SyncOutlined />} onClick={handleManualSyncTrigger}>
                            {t("send_to_exact")}
                          </Menu.Item>
                          <Menu.Item key="revert" icon={<CloseOutlined />} onClick={handleRevertFromExact}>
                            {t("revert_from_exact")}
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={["click"]}
                      disabled={isSyncingInvoices}
                    >
                      <Button type="default" loading={isSyncingInvoices} style={{ marginLeft: "auto" }}>
                        {!isMobile && t("bulk_operations")} <DownOutlined />
                      </Button>
                    </Dropdown>
                  </ButtonsWrapper>
                </>
              )}
            </Row>
            {isLoading || customersLoading ? (
              <Loading />
            ) : (
              <div className="table-responsive">
                <Table
                  showSorterTooltip={false}
                  columns={tableColumns}
                  dataSource={filteredInvoices}
                  rowKey="id"
                  scroll={{ x: 1300 }}
                  rowSelection={{
                    selectedRowKeys: selectedRowKeys,
                    type: "checkbox",
                    preserveSelectedRowKeys: false,
                    ...rowSelection,
                  }}
                  expandable={{
                    expandedRowRender,
                    expandIcon: ({ expanded, onExpand, record }) =>
                      record.isCombinedInvoice ? (
                        expanded ? (
                          <MinusSquareOutlined
                            onClick={(e) => onExpand(record, e)}
                            style={{ fontSize: "16px", color: "#1890ff", cursor: "pointer" }}
                          />
                        ) : (
                          <PlusSquareOutlined
                            onClick={(e) => onExpand(record, e)}
                            style={{ fontSize: "16px", color: "#1890ff", cursor: "pointer" }}
                          />
                        )
                      ) : null,
                    rowExpandable: (record) => record.isCombinedInvoice === true,
                  }}
                  pagination={{ showSizeChanger: true }}
                />
              </div>
            )}
            {!!invoice && (
              <Drawer
                title={`${t("invoice")} | ${invoice.farmorderCustomerName}`}
                placement="right"
                onClose={closeDrawer}
                open={visible}
                width={isMobile ? window.innerWidth : 850}
                styles={{ body: { padding: isMobile ? 2 : 16 } }}
              >
                <Descriptions
                  bordered
                  title={
                    <div className="flex-row">
                      {t("invoice_details")}{" "}
                      <Tag color={getInvoiceStatusColor(invoice.status)}>{getInvoiceStatusText(invoice.status)}</Tag>
                    </div>
                  }
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("invoice_id")}>
                    {profile?.userRole === roles.superAdmin ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const win = window.open("", "_blank");
                          win.document.write(`<pre>${JSON.stringify(invoice, null, 2)}</pre>`);
                          win.document.close();
                        }}
                        style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
                      >
                        {invoice.id}
                      </a>
                    ) : (
                      invoice.id
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={invoice.farmorderIds?.length > 1 ? t("order_ids") : t("order_id")}>
                    {invoice.farmorderIds?.length > 1 ? invoice.farmorderIds.join(", ") : invoice.farmorderIds[0]}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("invoice_date")}>
                    {dayjs(invoice.invoiceDate).format("D MMM YYYY")}
                  </Descriptions.Item>
                  {!!invoice.edifactOrderNumber && (
                    <Descriptions.Item label={t("edi_log_order_number")}>
                      <Link
                        to={`${APP_PREFIX_PATH}/edilog?edifactOrderNumber=${encodeURIComponent(
                          invoice.edifactOrderNumber,
                        )}`}
                      >
                        {invoice.edifactOrderNumber}
                      </Link>
                    </Descriptions.Item>
                  )}
                  {!!invoice.edifactOrderNumber && (
                    <Descriptions.Item label={t("edi_invoic_sent_at")}>
                      {invoice.edifactInvoicSentAt
                        ? dayjs.unix(invoice.edifactInvoicSentAt).format("D MMM YYYY HH:mm")
                        : "-"}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t("company")}>{invoice.farmorderCustomerName}</Descriptions.Item>
                  <Descriptions.Item label={t("delivery_date")}>
                    {dayjs(invoice.farmorderDeliveryDate, "YYYY-MM-DD").format("ddd D MMM YYYY")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("payment_method")}>{invoice.paymentMethod || "-"}</Descriptions.Item>
                  <Descriptions.Item label={t("orders_included")}>
                    {invoice.farmorderIds ? invoice.farmorderIds.length : 0}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("customer_discount")}>{invoice.customerDiscount || 0}%</Descriptions.Item>
                  <Descriptions.Item label={t("discount_code_applied")}>
                    {invoice.discountCodeApplied ? "Yes" : "No"}
                  </Descriptions.Item>
                  {invoice.discountCodeAmount > 0 && (
                    <Descriptions.Item label={t("discount_code_amount")}>
                      {invoice.discountCodeAmount || 0}
                    </Descriptions.Item>
                  )}
                  {invoice.discountCodePercentage > 0 && (
                    <Descriptions.Item label={t("discount_code_percentage")}>
                      {invoice.discountCodePercentage || 0}%
                    </Descriptions.Item>
                  )}
                  {invoice.complaintId && (
                    <Descriptions.Item label={t("complaint_id")}>
                      <button
                        className="text-button"
                        onClick={() => openComplaintDetails(invoice.complaintId)}
                        style={{ padding: 0 }}
                      >
                        {invoice.complaintId}
                      </button>
                    </Descriptions.Item>
                  )}
                  {invoice.invoiceType === "credit" && (
                    <Descriptions.Item label={t("invoice_type")}>{t("credit")}</Descriptions.Item>
                  )}
                  {invoice.isCombinedInvoice && (
                    <>
                      <Descriptions.Item label={t("invoice_type")}>
                        <Tag color="purple">{t("combined_invoice")}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label={t("combined_from")}>
                        {invoice.combinedFromInvoiceIds?.length || 0} {t("invoices")}
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>

                {/* Combined invoice details breakdown */}
                {invoice.isCombinedInvoice && invoice.combinedInvoiceDetails && (
                  <Descriptions
                    bordered
                    title={<div className="flex-row mt-4">{t("combined_invoice_breakdown")}</div>}
                    size="small"
                    column={1}
                  >
                    <div className="table-responsive">
                      <Table
                        showSorterTooltip={false}
                        columns={[
                          {
                            title: t("order_id"),
                            dataIndex: "orderId",
                          },
                          {
                            title: t("delivery_date"),
                            dataIndex: "deliveryDate",
                            render: (date) => dayjs(date).format("D MMM YYYY"),
                          },
                          {
                            title: t("products"),
                            dataIndex: "products",
                            render: (products) => `${products?.length || 0} items`,
                          },
                          {
                            title: t("reference"),
                            dataIndex: "reference",
                            render: (ref) => ref || "-",
                          },
                          {
                            title: t("subtotal"),
                            dataIndex: "subTotal",
                            render: (amount) => CURRENCY + formatPrice(amount),
                          },
                        ]}
                        dataSource={invoice.combinedInvoiceDetails}
                        rowKey="orderId"
                        pagination={false}
                        size="small"
                      />
                    </div>
                  </Descriptions>
                )}

                {!!invoice.userShoppingCart && (
                  <Descriptions
                    bordered
                    title={
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className="flex-row mt-4">{t("products")}</div>
                        {hasUnsavedPriceChanges && profile?.userRole === roles.superAdmin && (
                          <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={handleCancelPriceChanges}
                              disabled={isSavingPrices}
                            >
                              {t("cancel")}
                            </Button>
                            <Button
                              type="primary"
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={handleSavePriceChanges}
                              loading={isSavingPrices}
                            >
                              {t("save")}
                            </Button>
                          </div>
                        )}
                      </div>
                    }
                    size="small"
                    column={1}
                  >
                    <div className="table-responsive">
                      <Table
                        showSorterTooltip={false}
                        columns={tableColumnsDetails}
                        dataSource={invoice.userShoppingCart}
                        rowKey={(record) => (record.isCreditLine ? `${record.productId}_credit` : record.productId)}
                        pagination={false}
                        summary={(pageData) => {
                          const itemsAmount = pageData.length;
                          const displayInvoice = recalculatedInvoice || invoice;
                          const canEditPrices = profile?.userRole === roles.superAdmin && invoice?.status === "draft";
                          const creditedProducts = pageData.filter((item) => item.isCreditLine || item.itemCount < 0);
                          const creditedTotal = creditedProducts.reduce((sum, item) => {
                            const price = parseFloat(item.productPrice || item.price || 0);
                            const itemTotal = item.itemCount * price;
                            return sum + itemTotal;
                          }, 0);
                          const hasCreditedProducts = creditedProducts.length > 0;

                          return (
                            !!invoice && (
                              <>
                                {hasCreditedProducts && (
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell>
                                      <Text strong>{t("credited_total")}</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell>
                                      <Text strong style={{ color: "#ff4d4f" }}>
                                        {CURRENCY}-{formatPrice(Math.abs(creditedTotal))}
                                      </Text>
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                )}
                                <Table.Summary.Row>
                                  <Table.Summary.Cell>
                                    <Text strong>{t("subtotal")}</Text>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell />
                                  <Table.Summary.Cell />
                                  <Table.Summary.Cell>
                                    <Text strong>{CURRENCY + formatPrice(displayInvoice.subTotalAmount)}</Text>
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                                {!!invoice.discountCodeAmount && (
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell>
                                      <Text strong>{t("discount")} (€)</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell>
                                      <Text strong>
                                        {CURRENCY}-{formatPrice(invoice.discountCodeAmount)}
                                      </Text>
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                )}
                                {!!invoice.discountCodePercentage && (
                                  <Table.Summary.Row>
                                    <Table.Summary.Cell>
                                      <Text strong>{t("discount")} (%)</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell />
                                    <Table.Summary.Cell>
                                      <Text strong>
                                        {!!invoice.discountCodePercentage
                                          ? formatPrice(invoice.discountCodePercentage) + " %"
                                          : "0 %"}
                                      </Text>
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                )}
                                <Table.Summary.Row>
                                  <Table.Summary.Cell>
                                    <Text strong>{t("shipping")}</Text>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell />
                                  <Table.Summary.Cell />
                                  <Table.Summary.Cell>
                                    {canEditPrices && invoice.shippingCost ? (
                                      editingShippingField ? (
                                        <InputNumber
                                          autoFocus
                                          value={
                                            editingShippingCost !== null
                                              ? editingShippingCost
                                              : parseFloat(invoice.shippingCost)
                                          }
                                          onChange={(value) => {
                                            if (value !== null) {
                                              setEditingShippingCost(value);
                                            }
                                          }}
                                          onBlur={() => setEditingShippingField(false)}
                                          onPressEnter={() => setEditingShippingField(false)}
                                          precision={2}
                                          min={0}
                                          step={0.01}
                                          style={{ width: "100px", fontWeight: 600 }}
                                          prefix="€"
                                        />
                                      ) : (
                                        <Text
                                          strong
                                          onClick={() => setEditingShippingField(true)}
                                          style={{
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            textDecorationStyle: "dashed",
                                          }}
                                        >
                                          {CURRENCY +
                                            formatPrice(
                                              editingShippingCost !== null ? editingShippingCost : invoice.shippingCost,
                                            )}
                                        </Text>
                                      )
                                    ) : (
                                      <Text strong>
                                        {CURRENCY + formatPrice(parseFloat(invoice.shippingCost || 0))}
                                      </Text>
                                    )}
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                                <Table.Summary.Row>
                                  <Table.Summary.Cell>
                                    <Text strong={true}>{t("total")}</Text>
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell>{itemsAmount}</Table.Summary.Cell>
                                  <Table.Summary.Cell />
                                  <Table.Summary.Cell>
                                    <Text strong={true}>{CURRENCY + formatPrice(displayInvoice.totalAmount)}</Text>
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                              </>
                            )
                          );
                        }}
                      />
                    </div>
                  </Descriptions>
                )}
              </Drawer>
            )}
          </Card>
        </Col>
      </Row>
      <ButtonsWrapper style={{ justifyContent: "start", paddingBottom: "2rem" }}>
        <Button onClick={() => handlePeriodChange(30)} type={maxDaysAgo === 30 ? "primary" : "default"}>
          {t("last_30_days")}
        </Button>
        <Button onClick={() => handlePeriodChange(90)} type={maxDaysAgo === 90 ? "primary" : "default"}>
          {t("last_90_days")}
        </Button>
        <RangePicker onChange={handleCustomDateRange} placeholder={[t("start_date"), t("end_date")]} />
      </ButtonsWrapper>
      <BulkExactModal
        visible={bulkExactModalVisible}
        onClose={() => closeBulkExactModal("invoice")}
        processing={bulkExactProcessing}
        progress={bulkExactProgress}
        currentItem={bulkExactCurrentItem}
        results={bulkExactResults}
        itemType="invoice"
      />
      <CreditInvoiceModal
        visible={creditInvoiceModalOpen}
        onClose={handleCloseCreditInvoiceModal}
        invoice={selectedInvoiceForCredit}
        users={users}
        companyFilter={companyFilter}
      />
      <ComplaintDetailsModal
        visible={complaintModalVisible}
        onClose={() => {
          setComplaintModalVisible(false);
          setComplaintData(null);
        }}
        complaintData={complaintData}
        loading={complaintLoading}
      />
      <SendInvoicModal
        visible={!!sendInvoicInvoice}
        invoice={sendInvoicInvoice}
        onCancel={() => setSendInvoicInvoice(null)}
        onSent={() => setSendInvoicInvoice(null)}
      />
      <CombineInvoicesModal
        visible={combineInvoicesModalOpen}
        onClose={() => setCombineInvoicesModalOpen(false)}
        onSuccess={handleCombineSuccess}
        selectedInvoices={selectedRows}
        profile={profile}
      />
      {/* Manual Credit Invoice Modal */}
      <CreditInvoiceModal
        visible={manualCreditModalOpen}
        onClose={() => setManualCreditModalOpen(false)}
        invoice={null}
        users={users}
        companyFilter={companyFilter}
      />
      {/* Manual Exact ID Modal */}
      <Modal
        title={t("set_to_in_exact")}
        open={manualExactModalOpen}
        onOk={handleManualExactSubmit}
        onCancel={() => {
          setManualExactModalOpen(false);
          setSelectedInvoiceForExact(null);
          manualExactForm.resetFields();
        }}
        okText={t("save")}
        cancelText={t("cancel")}
      >
        <Form form={manualExactForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="exactInvoiceId"
            label={t("exact_invoice_id")}
            rules={[{ required: true, message: t("please_enter_exact_invoice_id") }]}
          >
            <Input placeholder={t("enter_exact_invoice_id")} />
          </Form.Item>
          <Form.Item
            name="exactSalesOrderNumber"
            label={t("exact_sales_order_number")}
            rules={[{ required: true, message: t("please_enter_exact_sales_order_number") }]}
          >
            <Input placeholder={t("enter_exact_sales_order_number")} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 576px) {
    //flex-direction: column;
    align-items: flex-start;
  }
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;

export default Invoices;
